import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Shield, 
  Phone, 
  Star, 
  IndianRupee,
  Users,
  Hospital,
  CheckCircle,
  AlertTriangle,
  Calculator,
  FileText,
  Award,
  TrendingUp,
  Heart,
  Search
} from 'lucide-react';

const Insurance = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAge, setSelectedAge] = useState('');
  const [selectedCoverage, setSelectedCoverage] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');

  // Comprehensive Indian Health Insurance Providers (Based on IRDAI FY23-24 data)
  const insuranceProviders = [
    {
      id: 'star-health',
      name: 'Star Health and Allied Insurance Co. Ltd.',
      logo: '⭐',
      type: 'Standalone Health',
      claimSettlementRatio: '82.31%',
      networkHospitals: '14,000+',
      speciality: 'India\'s largest standalone health insurer',
      website: 'starhealth.in',
      customerCare: '1800-425-2255'
    },
    {
      id: 'navi-general',
      name: 'Navi General Insurance Ltd.',
      logo: '🚢',
      type: 'General (Health Plans)',
      claimSettlementRatio: '99.97%',
      networkHospitals: 'Extensive network (locator available)',
      speciality: 'Highest claim settlement ratio in FY23-24',
      website: 'naviinsurance.com',
      customerCare: '1800-123-0004'
    },
    {
      id: 'acko-general',
      name: 'Acko General Insurance Ltd.',
      logo: '🎯',
      type: 'General (Health Plans)',
      claimSettlementRatio: '99.91%',
      networkHospitals: 'Digital network locator available',
      speciality: 'Digital-first insurance with excellent CSR',
      website: 'ackoinsurance.com',
      customerCare: '1800-266-2256'
    },
    {
      id: 'reliance-general',
      name: 'Reliance General Insurance Co. Ltd.',
      logo: '🏭',
      type: 'General (Health Plans)',
      claimSettlementRatio: '99.57%',
      networkHospitals: 'Pan-India network (locator available)',
      speciality: 'Strong financial backing with excellent CSR',
      website: 'reliancegeneral.co.in',
      customerCare: '1800-300-24-24'
    },
    {
      id: 'hdfc-ergo',
      name: 'HDFC ERGO General Insurance Co. Ltd.',
      logo: '🏦',
      type: 'General (Health Plans)',
      claimSettlementRatio: '99.16%',
      networkHospitals: 'Extensive network (locator available)',
      speciality: 'Leading general insurer with comprehensive health plans',
      website: 'hdfcergo.com',
      customerCare: '1800-2121-206'
    },
    {
      id: 'universal-sompo',
      name: 'Universal Sompo General Insurance Co. Ltd.',
      logo: '🌏',
      type: 'General (Health Plans)',
      claimSettlementRatio: '98.11%',
      networkHospitals: 'Network locator available',
      speciality: 'Strong Japanese-Indian joint venture',
      website: 'universalsompo.com',
      customerCare: '1800-103-0000'
    },
    {
      id: 'united-india',
      name: 'United India Insurance Co. Ltd.',
      logo: '🇮🇳',
      type: 'General (Health Plans)',
      claimSettlementRatio: '96.33%',
      networkHospitals: 'Government network (locator available)',
      speciality: 'Established PSU insurer with nationwide presence',
      website: 'uiic.co.in',
      customerCare: '1800-425-4141'
    },
    {
      id: 'aditya-birla',
      name: 'Aditya Birla Health Insurance Co. Ltd.',
      logo: '🏢',
      type: 'Standalone Health',
      claimSettlementRatio: '92.97%',
      networkHospitals: '10,051+',
      speciality: 'Focus on preventive healthcare and wellness',
      website: 'adityabirlacapital.com',
      customerCare: '1800-270-2700'
    },
    {
      id: 'care-health',
      name: 'Care Health Insurance Ltd. (formerly Religare)',
      logo: '💚',
      type: 'Standalone Health',
      claimSettlementRatio: '92.77%',
      networkHospitals: '11,400+',
      speciality: 'Comprehensive health coverage with personalized care',
      website: 'careinsurance.com',
      customerCare: '1800-102-4488'
    },
    {
      id: 'niva-bupa',
      name: 'Niva Bupa Health Insurance Co. Ltd.',
      logo: '💙',
      type: 'Standalone Health',
      claimSettlementRatio: '92.02%',
      networkHospitals: 'Extensive network (locator available)',
      speciality: 'Global healthcare expertise with local presence',
      website: 'nivabupa.com',
      customerCare: '1800-103-2434'
    },
    {
      id: 'icici-lombard',
      name: 'ICICI Lombard General Insurance Co. Ltd.',
      logo: '🏢',
      type: 'General (Health Plans)',
      claimSettlementRatio: 'Available in market reports',
      networkHospitals: '10,200+',
      speciality: 'Leading private general insurer',
      website: 'icicilombard.com',
      customerCare: '1800-2666'
    },
    {
      id: 'manipal-cigna',
      name: 'ManipalCigna Health Insurance Co. Ltd.',
      logo: '🏥',
      type: 'Standalone Health',
      claimSettlementRatio: 'Market competitive rates',
      networkHospitals: '8,500+',
      speciality: 'Hospital group backed health insurance',
      website: 'manipalcigna.com',
      customerCare: '1800-102-4488'
    },
    {
      id: 'oriental-insurance',
      name: 'The Oriental Insurance Co. Ltd.',
      logo: '🕌',
      type: 'General (Health Plans)',
      claimSettlementRatio: '65.08%',
      networkHospitals: 'Government network (locator available)',
      speciality: 'Government PSU with affordable plans',
      website: 'orientalinsurance.org.in',
      customerCare: '1800-118-485'
    },
    {
      id: 'bajaj-allianz',
      name: 'Bajaj Allianz General Insurance Co. Ltd.',
      logo: '🛡️',
      type: 'General (Health Plans)',
      claimSettlementRatio: 'Competitive market rates',
      networkHospitals: 'Extensive network (locator available)',
      speciality: 'Strong distribution network across India',
      website: 'bajajallianz.com',
      customerCare: '1800-209-5858'
    },
    {
      id: 'go-digit',
      name: 'Go Digit General Insurance Ltd.',
      logo: '📱',
      type: 'General (Health Plans)',
      claimSettlementRatio: 'Market competitive rates',
      networkHospitals: 'Digital network (locator available)',
      speciality: 'Digital-first approach with innovative products',
      website: 'godigit.com',
      customerCare: '1800-258-4242'
    },
    {
      id: 'future-generali',
      name: 'Future Generali India Insurance Co. Ltd.',
      logo: '🔮',
      type: 'General (Health Plans)',
      claimSettlementRatio: 'IRDAI regulated rates',
      networkHospitals: 'Pan-India coverage',
      speciality: 'International expertise with local understanding',
      website: 'futuregenerali.in',
      customerCare: '1800-220-233'
    },
    {
      id: 'new-india-assurance',
      name: 'The New India Assurance Co. Ltd.',
      logo: '🆕',
      type: 'General (Health Plans)',
      claimSettlementRatio: 'Government regulated rates',
      networkHospitals: 'Nationwide PSU network',
      speciality: 'Oldest general insurance company in India',
      website: 'newindia.co.in',
      customerCare: '1800-209-1111'
    },
    {
      id: 'kotak-general',
      name: 'Kotak Mahindra General Insurance Co. Ltd.',
      logo: '🏛️',
      type: 'General (Health Plans)',
      claimSettlementRatio: 'Market competitive rates',
      networkHospitals: 'Network locator available',
      speciality: 'Banking group backed insurance services',
      website: 'kotakgeneral.com',
      customerCare: '1800-266-4545'
    },
    {
      id: 'liberty-general',
      name: 'Liberty General Insurance Ltd.',
      logo: '🗽',
      type: 'General (Health Plans)',
      claimSettlementRatio: 'IRDAI compliant rates',
      networkHospitals: 'Network locator available',
      speciality: 'International insurance expertise',
      website: 'libertyinsurance.in',
      customerCare: '1800-266-5844'
    },
    {
      id: 'tata-aig',
      name: 'Tata AIG General Insurance Co. Ltd.',
      logo: '🏭',
      type: 'General (Health Plans)',
      claimSettlementRatio: 'Available in market reports',
      networkHospitals: 'Network locator available',
      speciality: 'Tata group backed with AIG expertise',
      website: 'tataaig.com',
      customerCare: '1800-266-7780'
    }
  ];

  const governmentSchemes = [
    {
      name: 'Ayushman Bharat (PM-JAY)',
      coverage: '₹5 Lakh per family per year',
      eligibility: 'Families as per SECC database',
      benefits: ['Cashless treatment', '1,400+ procedures', 'Secondary & tertiary care'],
      website: 'pmjay.gov.in',
      contact: '14555'
    },
    {
      name: 'ESIC (Employee State Insurance)',
      coverage: 'Comprehensive medical care',
      eligibility: 'Employees earning ≤ ₹25,000/month',
      benefits: ['Family coverage', 'Maternity benefits', 'Disability benefits'],
      website: 'esic.nic.in',
      contact: '1800-11-2526'
    },
    {
      name: 'CGHS (Central Government Health Scheme)',
      coverage: 'Comprehensive healthcare',
      eligibility: 'Central government employees',
      benefits: ['Pensioners covered', 'Dependents included', 'Cashless treatment'],
      website: 'cghs.nic.in',
      contact: '1800-11-0094'
    }
  ];

  const handleGetQuote = (provider) => {
    const message = `Health Insurance Quote Request

Provider: ${provider.name}
Contact: ${provider.customerCare}
Website: ${provider.website}

Customer Details:
Name: [Please provide]
Age: ${selectedAge || '[Please specify]'}
Preferred Coverage: ${selectedCoverage || '[Please specify]'}

I'm interested in getting a detailed quote and understanding the policy terms. Please share the best plans suitable for my requirements.

GEETH365 Reference: Healthcare insurance inquiry`;

    const whatsAppLink = `https://wa.me/917337541113?text=${encodeURIComponent(message)}`;
    window.open(whatsAppLink, '_blank');
  };

  const filteredProviders = insuranceProviders.filter(provider =>
    provider.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    provider.speciality.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Health Insurance in India
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Compare top health insurance providers across India. Find the best coverage for you and your family 
              with expert guidance from GEETH365.
            </p>
          </div>

          {/* Search and Filters */}
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Search insurance providers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={selectedAge} onValueChange={setSelectedAge}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Age Group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="18-25">18-25 years</SelectItem>
                  <SelectItem value="26-35">26-35 years</SelectItem>
                  <SelectItem value="36-45">36-45 years</SelectItem>
                  <SelectItem value="46-55">46-55 years</SelectItem>
                  <SelectItem value="56-65">56-65 years</SelectItem>
                  <SelectItem value="65+">65+ years</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedCoverage} onValueChange={setSelectedCoverage}>
                <SelectTrigger>
                  <SelectValue placeholder="Coverage Amount" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2-5L">₹2-5 Lakh</SelectItem>
                  <SelectItem value="5-10L">₹5-10 Lakh</SelectItem>
                  <SelectItem value="10-25L">₹10-25 Lakh</SelectItem>
                  <SelectItem value="25L+">₹25 Lakh+</SelectItem>
                </SelectContent>
              </Select>

              <Button className="bg-orange-600 hover:bg-orange-700">
                <Calculator className="w-4 h-4 mr-2" />
                Compare Plans
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Government Schemes */}
        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-blue-800">
              <Award className="w-6 h-6" />
              <span>Government Health Insurance Schemes</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {governmentSchemes.map((scheme, index) => (
                <Card key={index} className="border-blue-200 bg-white">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-blue-900 mb-2">{scheme.name}</h3>
                    <div className="space-y-2 text-sm">
                      <div><strong>Coverage:</strong> {scheme.coverage}</div>
                      <div><strong>Eligibility:</strong> {scheme.eligibility}</div>
                      <div className="space-y-1">
                        <strong>Benefits:</strong>
                        {scheme.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-start space-x-1">
                            <CheckCircle className="w-3 h-3 text-green-600 mt-1 flex-shrink-0" />
                            <span className="text-blue-700">{benefit}</span>
                          </div>
                        ))}
                      </div>
                      <div className="pt-2 border-t">
                        <div className="text-xs text-blue-600">
                          📞 {scheme.contact} | 🌐 {scheme.website}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Private Insurance Providers */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Private Health Insurance Providers</h2>
          
          {filteredProviders.map((provider) => (
            <Card key={provider.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Provider Info */}
                  <div>
                    <div className="flex items-center space-x-3 mb-4">
                      <span className="text-3xl">{provider.logo}</span>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{provider.name}</h3>
                        <Badge className="bg-blue-100 text-blue-700 text-xs">{provider.type}</Badge>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-600">Network Hospitals:</span>
                        <Badge className="bg-blue-100 text-blue-700 text-xs">{provider.networkHospitals}</Badge>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-600">Claim Settlement Ratio:</span>
                        <Badge className="bg-green-100 text-green-700 text-xs">{provider.claimSettlementRatio}</Badge>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-4">{provider.speciality}</p>
                    
                    <div className="text-xs text-gray-500 mb-4">
                      📞 {provider.customerCare} | 🌐 {provider.website}
                    </div>
                  </div>

                  {/* Action & Details */}
                  <div className="space-y-4">
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg">
                      <h4 className="font-semibold text-orange-900 mb-2">Quick Quote</h4>
                      <p className="text-sm text-orange-800 mb-3">
                        Get personalized health insurance quotes based on your age, family size, and coverage needs.
                      </p>
                      <Button 
                        onClick={() => handleGetQuote(provider)}
                        className="w-full bg-orange-600 hover:bg-orange-700"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        Get Quote via WhatsApp
                      </Button>
                    </div>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-green-900 mb-2">Key Features</h4>
                      <ul className="space-y-1 text-sm text-green-800">
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="w-3 h-3 text-green-600 mt-1 flex-shrink-0" />
                          <span>Cashless treatment at network hospitals</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="w-3 h-3 text-green-600 mt-1 flex-shrink-0" />
                          <span>Pre & post hospitalization coverage</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="w-3 h-3 text-green-600 mt-1 flex-shrink-0" />
                          <span>Day care procedures included</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="w-3 h-3 text-green-600 mt-1 flex-shrink-0" />
                          <span>Tax benefits under Section 80D</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Data Collection Methodology */}
        <Card className="mt-8 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-blue-800">
              <FileText className="w-6 h-6" />
              <span>Data Collection & Verification</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-blue-900 mb-3">Data Sources & Methodology</h4>
                <div className="space-y-3 text-sm text-blue-800">
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <strong>Claim Settlement Ratios (CSR):</strong> Based on IRDAI FY 2023-24 data as reported by The Economic Times
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <strong>Network Hospitals:</strong> Data collected exclusively from insurer's official websites where explicitly stated
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <strong>Verification:</strong> All information cross-referenced with IRDAI official listings
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-blue-900 mb-3">Data Transparency</h4>
                <div className="space-y-2 text-sm text-blue-800">
                  <p>• CSR data reflects performance within 3 months of FY 2023-24</p>
                  <p>• Network hospital counts include only explicitly claimed figures</p>
                  <p>• "Network locator available" indicates no single figure disclosed</p>
                  <p>• All data sourced from official regulatory and insurer communications</p>
                </div>
                <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                  <p className="text-xs text-blue-700">
                    <strong>Note:</strong> This comprehensive data helps you make informed decisions. 
                    For the most current rates and terms, contact insurers directly via the provided numbers.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Why Health Insurance Section */}
        <Card className="mt-12 bg-gradient-to-r from-green-50 to-green-100 border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-green-800">
              <Heart className="w-6 h-6" />
              <span>Why Health Insurance is Essential in India</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-green-900">Financial Protection</h4>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-green-800 text-sm">Average hospital bill in India: ₹1-5 lakhs</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-green-800 text-sm">Cancer treatment: ₹15-20 lakhs average</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-green-800 text-sm">Heart surgery: ₹3-8 lakhs average</span>
                </li>
              </ul>
            </div>
            <div className="space-y-4">
              <h4 className="font-semibold text-green-900">Tax Benefits</h4>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <IndianRupee className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-green-800 text-sm">Section 80D: Up to ₹25,000 deduction</span>
                </li>
                <li className="flex items-start space-x-2">
                  <IndianRupee className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-green-800 text-sm">Senior citizens: Up to ₹50,000 deduction</span>
                </li>
                <li className="flex items-start space-x-2">
                  <IndianRupee className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-green-800 text-sm">Preventive health checkups: ₹5,000 additional</span>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Insurance;